import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Upload, Brain, Bell, Users, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AccountSettingsPage() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = () => {
    api.get("/account/me").then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => { fetchData(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El archivo excede 2MB");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/account/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!data || !data.account) {
    return (
      <div className="space-y-6" data-testid="account-settings-page">
        <h1 className="font-heading font-black text-3xl tracking-tight">Mi Cuenta</h1>
        <Card className="rounded-none">
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            Tu usuario no está vinculado a una cuenta empresarial.
          </CardContent>
        </Card>
      </div>
    );
  }

  const { account, plan, usage } = data;
  const fmtLimit = (used, limit) => limit === -1 ? `${used} / ∞` : `${used} / ${limit}`;
  const renewalDate = account.renewal_date ? new Date(account.renewal_date).toLocaleDateString("es-GT", { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="account-settings-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight">Mi Cuenta</h1>
        <p className="text-sm text-muted-foreground font-mono mt-1">
          {account.company_name} · Plan {plan?.name}
        </p>
      </div>

      {/* Logo section */}
      <Card className="rounded-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Logo de mi empresa</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-6">
          <CompanyLogo logoUrl={account.logo_url} companyName={account.company_name} size={96} />
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">
              PNG, JPG o SVG · máximo 2MB. Aparece en tu panel lateral.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.webp"
              onChange={handleUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-none bg-[#002FA7] hover:bg-[#001D66]"
              data-testid="upload-logo-btn"
            >
              <Upload size={16} className="mr-2" />
              {uploading ? "Subiendo..." : account.logo_url ? "Cambiar logo" : "Subir logo"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Plan & renewal */}
        <Card className="rounded-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Plan activo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge className="rounded-none bg-[#002FA7] text-white font-mono text-[10px]">{plan?.name?.toUpperCase()}</Badge>
              <span className="text-xs font-mono text-muted-foreground">
                {account.billing_cycle === "annual" ? "Pago anual" : "Pago mensual"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={16} className="text-muted-foreground" />
              <span className="text-muted-foreground">Renovación:</span>
              <span className="font-mono">{renewalDate}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              {plan?.max_users === 1 ? "1 usuario" : `Hasta ${plan?.max_users} usuarios`} · {plan?.currency} {(account.billing_cycle === "annual" ? plan?.price_annual : plan?.price_monthly)?.toLocaleString()} / {account.billing_cycle === "annual" ? "año" : "mes"}
            </p>
          </CardContent>
        </Card>

        {/* Usage this month */}
        <Card className="rounded-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Consumo del mes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain size={16} className="text-muted-foreground" />
                Análisis IA
              </div>
              <span className="font-mono">{fmtLimit(usage?.ai_analyses_used || 0, plan?.ai_analyses_per_month)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-muted-foreground" />
                Inteligencia de Proveedores
              </div>
              <span className="font-mono text-[#2A9D8F]">Libre (demo)</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-muted-foreground" />
                Alertas activas
              </div>
              <span className="font-mono">{fmtLimit(usage?.alerts_active || 0, plan?.max_alerts)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
